import { encodeBase64 } from 'shared-utils/base64';
import { GTM_CONTAINER_ID as GTM_ID } from './constants';
import {
  type OsanoConsentWindow,
  getLegacyOneTrustGroups,
} from './osanoConsentBridge';

type ConsentValue = 'U' | 'G' | 'D';

// Keep the Gartner container available only for the defined production rollback
// window after Capterra switches to the G2-owned container.
const LEGACY_GTM_CONTAINER_ID = 'GTM-NS6WSW4V';

type ConsentWindow = Window &
  OsanoConsentWindow & {
    google_tag_manager?: Record<
      string,
      {
        dataLayer?: {
          get?: (key: string) => unknown;
        };
      }
    >;
    GDMGetConsent?: () => Record<string, ConsentValue> | null;
    google_tag_data?: {
      ics?: {
        getConsentState: (
          key: string,
          options: { containerScopedDefaults: Record<string, never> }
        ) => number;
      };
    };
  };

const getLaunchDarklyData = (windowObj: ConsentWindow) => {
  const gtmObject =
    // Prefer the active G2 container. The legacy container is a temporary
    // fallback for rollback while pages with either deployment may be served.
    windowObj?.google_tag_manager?.[GTM_ID] ||
    windowObj?.google_tag_manager?.[LEGACY_GTM_CONTAINER_ID];
  const get = gtmObject?.dataLayer?.get;

  if (gtmObject) {
    const experimentUserId = get?.('GAExperimentUserId');
    const experimentId = get?.('GAExperimentId');

    return {
      GAExperimentUserId:
        typeof experimentUserId === 'string' || experimentUserId == null
          ? experimentUserId
          : undefined,
      GAExperimentId:
        typeof experimentId === 'string' || experimentId == null
          ? experimentId
          : undefined,
    };
  }

  return null;
};

const mapper: Record<number, ConsentValue> = {
  0: 'U', // Undetermined
  1: 'G', // Granted
  2: 'D', // Denied
  3: 'G', // Implicit Granted -> Granted
  4: 'D', // Implicit Denied -> Denied
};

export const getConsentData = (windowObj: ConsentWindow) => {
  try {
    if (windowObj.GDMGetConsent) {
      return windowObj.GDMGetConsent();
    }
    if (windowObj.google_tag_data?.ics) {
      return [
        'ad_storage',
        'analytics_storage',
        'functionality_storage',
        'personalization_storage',
        'security_storage',
        'ad_user_data',
        'ad_personalization',
      ].reduce<Record<string, ConsentValue>>((acc, key) => {
        return {
          ...acc,
          [key]:
            mapper[
              windowObj.google_tag_data.ics.getConsentState(key, {
                /**
                 * getConsentState method expects this parameter on a edge case scenario that we don't know yet how to reproduce
                 * https://app.datadoghq.com/rum/error-tracking/issue/9d4520cc-4496-11ef-aab3-da7ad0900002?query=containerScopedDefaults%20service%3Abx-capterra-www&fromUser=false&refresh_mode=sliding&view=spans&from_ts=1721030125556&to_ts=1721634915253&live=true
                 */
                containerScopedDefaults: {},
              })
            ] || 'U',
        };
      }, {});
    }
  } catch {
    return null;
  }
  return null;
};

const getConsentModeData = (windowObj: ConsentWindow) => {
  const capturedData = {
    LaunchDarkly: getLaunchDarklyData(windowObj),
    OneTrust: getLegacyOneTrustGroups(windowObj),
    Google: getConsentData(windowObj),
  };

  return encodeBase64(JSON.stringify(capturedData));
};

export default getConsentModeData;
